import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchHeaderFooterImageList } from "services/headerFooterDetail";
import { STALE_TIME } from "utils/constants";
import { fetchSignature } from "services/doctor";
import zoneLocalStorageId from "utils/zone";
import { fetchShowDummyReportData } from "services/investigationManager";
import "./sample.css";

const InvoicePage = ({ onClose, rowData, zoneRegistrationData }) => {
  const navigate = useNavigate();
  const search = useLocation().search;
  const display_header = new URLSearchParams(search).get("display_header");
  const display_banner = new URLSearchParams(search).get("display_banner");
  const display_footer = new URLSearchParams(search).get("display_footer");
  const display_footer_banner = new URLSearchParams(search).get(
    "display_footer_banner"
  );
  const display_address = new URLSearchParams(search).get("display_address");
  const display_watermark = new URLSearchParams(search).get(
    "display_watermark"
  );
  const token = new URLSearchParams(search).get("token");
  const display_header_data = new URLSearchParams(search).get(
    "display_header_data"
  );
  const investigationIds = new URLSearchParams(search).getAll(
    "investigation_ids"
  );
  const appointment = new URLSearchParams(search).get("appointment_id");

  const dispatch = useDispatch();
  const { barcode_detail } = useSelector((state) => state.dropListReducer);

  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);

  const _token =
    jwtAuthreducer?.token_values?.sub &&
    JSON.parse(jwtAuthreducer?.token_values?.sub?.replace(/'/g, '"'));

  const componentRef = useRef();

  const downloadPDF = async () => {
    try {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
      document.head.appendChild(script);

      script.onload = () => {
        const element = componentRef.current;
        const opt = {
          margin: 0,
          filename: "medical-report.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
          },
          jsPDF: {
            unit: "mm",
            format: "letter",
            orientation: "portrait",
          },
        };

        window
          .html2pdf()
          .set(opt)
          .from(element)
          .save()
          .then(() => {
            navigate("/investigation-manager");
          });
      };
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  function convertSecondsToAge(seconds) {
    const secondsInYear = 31536000;
    const secondsInMonth = 2629746;
    const secondsInDay = 86400;

    const years = Math.floor(seconds / secondsInYear);
    seconds %= secondsInYear;

    const months = Math.floor(seconds / secondsInMonth);
    seconds %= secondsInMonth;

    const days = Math.floor(seconds / secondsInDay);

    return { years, months, days };
  }

  function convertTimestampToDatetimeLocal(timestamp, showTime) {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    let formattedDate = `${day}-${month}-${year}`;

    if (showTime) {
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? String(hours).padStart(2, "0") : "12";

      formattedDate += ` ${hours}:${minutes}:${seconds} ${ampm}`;
    }

    return formattedDate;
  }

  const investigationId = Number(investigationIds[0]);

  const {
    data: pdfData,
    isLoading,
    refetch,
  } = useQuery(
    ["fetch-report-pdf-dummy-data", investigationId],
    () => fetchShowDummyReportData(investigationId),
    {
      staleTime: STALE_TIME,
      select: (res) => res?.data?.[0],
      onError: (res) => {
        console.error("Error fetching dummy PDF data:", res);
      },
      enabled: !!investigationId,
    }
  );

  useEffect(() => {
    if (pdfData) {
      setTimeout(() => {
        downloadPDF();
      }, 500);
    }
  }, [pdfData]);

  //   console.log("investigationIds", investigationId);

  const ageDetails = convertSecondsToAge(pdfData?.patient_obj?.age || 0);

  const { data } = useQuery(
    ["fetch-header-footer-list"],
    () =>
      fetchHeaderFooterImageList({
        zone_id: zoneLocalStorageId(),
        is_active: true,
        created_by: jwtAuthreducer.user.employee_code_id,
        token,
      }),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {},
      onError: (err) => {
        console.error("Error fetching Header Footer:", err);
      },
    }
  );

  //   useEffect(() => {
  //     let barcodeData = {
  //       barcode_ids: [100000000005],
  //     };
  //     dispatch(getBarcodeImage({ token, barcodeData }));
  //   }, []);

  // const doctorId = pdfData?.appointment_investigation_obj[0]?.
  //   observations_obj[0]?.doctor_id
  const doctorId =
    pdfData?.appointment_investigation_obj
      ?.flatMap((investigation) => investigation.observations_obj || [])
      ?.find((observation) => observation?.doctor_id)?.doctor_id || null;
  // console.log("doctorid", doctorId)
  // const { data: doctorSigData, isError, error } = useQuery(
  //   ["fetch-signature", doctorId],
  //   () => fetchSignature({ doctor_id: doctorId }),
  //   {
  //     staleTime: STALE_TIME,
  //     enabled: !!doctorId && rowData?.doctor_type !== "EXTERNAL",
  //   }
  // );

  const {
    data: doctorSigData,
    isError,
    error,
  } = useQuery(
    ["fetch-signature", doctorId],
    () =>
      fetchSignature({ doctor_id: doctorId, zone_id: rowData?.zone_id, token }),
    {
      staleTime: STALE_TIME,
      enabled: !!doctorId && rowData?.doctor_type !== "EXTERNAL",
    }
  );

  const groupedData = pdfData?.appointment_investigation_obj?.reduce(
    (acc, item) => {
      const department = item?.lab_department_name || "Unknown Department";

      if (!acc[department]) {
        acc[department] = [];
      }

      acc[department].push(item);
      return acc;
    },
    {}
  );

  // console.log("pdfData", pdfData);

  return (
    <div>
      <style
        dangerouslySetInnerHTML={{
          __html:
            '\n      html {\n        font-family: Helvetica, Segoe UI, sans-serif;\n        /* 1 */\n        line-height: 1.5;\n        /* 2 */\n        -ms-text-size-adjust: 100%;\n        /* 3 */\n        -webkit-text-size-adjust: 100%;\n        /* 3 */\n        -webkit-font-smoothing: antialiased;\n      }\n\n      body {\n    -webkit-print-color-adjust: exact;\n    print-color-adjust: exact;\n  width: 100%;\n        font-family: "Helvetica";\n        margin: 0 auto;\n        font-size: 14px;\n        font-weight: 400;\n        line-height: 1.5;\n        color: #000000;\n        background: #ffffff;\n        user-select: none;\n        -webkit-overflow-scrolling: touch;\n      }\n\n      html,\n      body {\n        height: 100%;\n      }\n\n      ::-webkit-scrollbar {\n        display: none;\n      }\n\n      img {\n        max-width: 100%;\n        border-style: none;\n      }\n\n      dl,\n      ol,\n      ul {\n        margin-bottom: 24px;\n      }\n\n      /**\n* 1. Remove the gray background on active links in IE 10.\n* 2. Remove gaps in links underline in iOS 8+ and Safari 8+.\n*/\n\n      a {\n        background-color: transparent;\n        /* 1 */\n        -webkit-text-decoration-skip: objects;\n        /* 2 */\n        text-decoration: none;\n      }\n\n      /**\n* Remove the outline on focused links when they are also active or hovered\n* in all browsers (opinionated).\n*/\n\n      a:active,\n      a:hover {\n        outline-width: 0;\n      }\n\n      /**\n* Prevent the duplicate application of `bolder` by the next rule in Safari 6.\n*/\n\n      b,\n      strong {\n        font-weight: inherit;\n      }\n\n      /**\n* Add the correct font weight in Chrome, Edge, and Safari.\n*/\n\n      b,\n      strong {\n        font-weight: bolder;\n      }\n\n      sub,\n      sup {\n        font-size: 75%;\n        line-height: 0;\n        position: relative;\n        vertical-align: baseline;\n      }\n\n      sub {\n        bottom: -0.25em;\n      }\n\n      sup {\n        top: -0.5em;\n      }\n\n      img {\n        max-width: 100%;\n        border-style: none;\n      }\n\n      .h1,\n      h1 {\n        font-size: 2.5rem;\n        font-weight: bold;\n        margin-bottom: 24px;\n      }\n\n      .h2,\n      h2 {\n        font-size: 2rem;\n        font-weight: bold;\n        margin-bottom: 24px;\n      }\n\n      .h3,\n      h3 {\n        font-size: 1.75rem;\n        font-weight: bold;\n        margin-bottom: 24px;\n      }\n\n      /**\n* Hide the overflow in IE.\n*/\n\n      svg:not(:root) {\n        overflow: hidden;\n      }\n\n      /* Forms\n========================================================================== */\n\n      /**\n* 1. Change font properties to `inherit` in all browsers (opinionated).\n* 2. Remove the margin in Firefox and Safari.\n*/\n\n      button,\n      input,\n      optgroup,\n      select,\n      textarea {\n        font: inherit;\n        /* 1 */\n        margin: 0;\n        /* 2 */\n      }\n\n      button,\n      input {\n        /* 1 */\n        overflow: visible;\n      }\n\n      /**\n* Remove the inheritance of text transform in Edge, Firefox, and IE.\n* 1. Remove the inheritance of text transform in Firefox.\n*/\n\n      button,\n      select {\n        /* 1 */\n        text-transform: none;\n      }\n\n      /**\n* 1. Prevent a WebKit bug where (2) destroys native `audio` and `video`\n*    controls in Android 4.\n* 2. Correct the inability to style clickable types in iOS and Safari.\n*/\n\n      button,\nhtml [type=\'button\'],\n\n  /* 1 */\n\n[type=\'reset\'],\n[type=\'submit\'] {\n        -webkit-appearance: button;\n        /* 2 */\n      }\n\n      /**\n* Correct the cursor style of increment and decrement buttons in Chrome.\n*/\n\n      [type="number"]::-webkit-inner-spin-button,\n      [type="number"]::-webkit-outer-spin-button {\n        height: auto;\n      }\n\n      /**\n* 1. Correct the odd appearance in Chrome and Safari.\n* 2. Correct the outline style in Safari.\n*/\n\n      [type="search"] {\n        -webkit-appearance: textfield;\n        /* 1 */\n        outline-offset: -2px;\n        /* 2 */\n      }\n\n      /**\n* Remove the inner padding and cancel buttons in Chrome and Safari on OS X.\n*/\n\n      [type="search"]::-webkit-search-cancel-button,\n      [type="search"]::-webkit-search-decoration {\n        -webkit-appearance: none;\n      }\n\n      /**\n* 1. Correct the inability to style clickable types in iOS and Safari.\n* 2. Change font properties to `inherit` in Safari.\n*/\n\n      ::-webkit-file-upload-button {\n        -webkit-appearance: button;\n        /* 1 */\n        font: inherit;\n        /* 2 */\n      }\n\n      * {\n        -webkit-box-sizing: border-box;\n        -moz-box-sizing: border-box;\n        box-sizing: border-box;\n        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n      }\n\n      *:focus {\n        outline: none;\n      }\n      .container {\n        display: flex;\n        flex-direction: column;\n        position: relative;\n      }\n      .trademarkInfo {\n        width: 100%;\n        height: 250px;\n        position: absolute;\n        top: 50%;\n        left: 50%;\n        transform: translate(-50%, -50%);\n        z-index: -1;\n      }\n      .trademarkInfo img {\n        width: 100%;\n        height: 100%;\n      }\n .header-container {\n  display: flex;\n  flex-direction: column;\n  align-items:center;\n  width: 100%;\n}\n     header {\n        display: flex;\n        position:relative }\n      .headerLeft {\n        width: 300px;\n        height: 65px;\n        margin-top: 2px;\n        background: #57b0d8 url(../assets/pdfimg/blue-corner-bg.png) no-repeat top right;\n        background-size: 53px 84px;\n      }\n      .headerRight {\n        width: 100%;\n        height: 65px;\n        margin-top: 2px;\n        background: #57b0d8 url(../assets/pdfimg/blue-line-bg.png) no-repeat top left;\n        background-size: 121px 84px;\n      }\n      .logo {padding: 0 32px 0 15px;\n      } \n .coverImage {\n width: 100%;\n height: 50%; \n  margin-top: 13px;\n overflow: hidden;\n }\n \n .coverImage img { \n width: 100%; \n height: 100px; \n object-fit: cover; \n }  \n .footerImageContainer { \n width: 100%;  \n height: 150px; \n  margin-top: 5px;\n  margin-bottom: 10px;\n   \n} \n.footerImageContainer img {\n width: 100%;\n height: 100%;\n object-fit: cover;\n}\n  \n \n     .logo img {}\n      .barcodeInfo {\n        display: flex;\n        justify-content: flex-end;\n        width: 100%;\n        margin: 5px 0 5px;\n      }\n      .barcodeImage {\n        display: flex;\n        height: 40px;\n        margin-right: 70px;\n      }\n      .barcodeImage img {\n        height: 100%;\n      }\n      .mainSection {\n        display: flex;\n        flex-direction: column;\n        flex: 1;\n        width: 100%;\n        padding: 0 40px;\n        position: relative;\n      }\n      .header-container::before {\n        content: "Medicity Speciality Laboratory";\n        font-size: 10px;\n        color: #757575;\n        line-height: 1.3;\n        position: absolute;\n        top: 179px;\n        left: -35px;\n        transform: rotate(-90deg);\n      }\n     .header-container .can-details-section {\n        width: 92%;\n       gap: 15px;\n        display: flex;\n        padding: 5px 5px 5px;\n     border:   2px solid #000000 !important\n      }\n      .can-column-one {\n        width: 100%;\n        display: flex;\n        flex-direction: column;\n        justify-content: space-between;\n      }\n      .can-column-two {\n        width: 100%;\n        display: flex;\n        flex-direction: column;\n      }\n      .can-detail-row {\n        width: 100%;\n        display: flex;\n        gap: 10px;\n      }\n      .can-detail-label {\n        display: flex;\n        font-size: 13px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.4;\n      }\n      .can-detail-text {\n        display: flex;\n        font-size: 13px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.4;\n      }\n      .dep-title {\n        font-size: 13px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1.4;\n        text-align: center;\n        text-decoration: underline;\n        text-transform: uppercase;\n        margin-top: 2px;\n      }\n      .rep-head-info {\n        display: flex;\n        font-size: 12px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1.4;\n        padding: 3px 0 0;\n        margin-bottom: 10px;\n        border-bottom: 2px solid #afafaf;\n      }\n      .rep-column-1 {\n        width: 35%;\n        display: flex;\n        flex-direction: column;\n        padding: 0 5px 0 0;\n      }\n      .rep-column-2 {\n        width: 15%;\n        display: flex;\n        flex-direction: column;\n        padding: 0 5px 0 0;\n      }\n      .rep-column-3 {\n        font-size: 10px;\n        width: 15%;\n        display: flex;\n        flex-direction: column;\n        padding: 0 5px 0 0;\n      }\n      .rep-column-4 {\n        font-size: 10px;\n        width: 35%;\n        display: flex;\n        flex-direction: column;\n        padding: 0 0 0 2px;\n      }\n      .rep-details-row {\n        width: 100%;\n        display: flex;\n        font-size: 15px;\n        color: #000000;\n        font-weight: 400;\n        padding: 0 0 2px;\n      }\n      .name-title {\n        font-size: 10px;\n        color: #000000;\n        font-weight: 600;\n        text-decoration: underline;\n      }\n      .test-name {\n        font-size: 10px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1;\n      }\n      .test-name-details {\n        font-size: 10px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.3;\n      }\n      .rep-sample-type {\n        width: 100%;\n        display: flex;\n        flex-direction: column;\n        padding: 2px 0 6px;\n      }\n      .rep-test-type {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 10px;\n        color: #000000;\n        font-weight: 600;\n        font-style: italic;\n      }\n      .rep-test-details {\n        font-size: 11px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1;\n        padding-top: 4px;\n      }\n      .rep-end-report {\n        width: 100%;\n        border-top: 2px solid #000000;\n      }\n      .rep-end-text {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 15px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.3;\n        padding: 5px 0;\n        text-align: center;\n      }\n      footer {\n        width: 100%;\n        display: flex;\n        flex-direction: column;\n  }\n      .footerTopRow {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 12px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1;\n        padding: 0 0 0 18px;\n      }\n      .footer-doc-details {\n  width: 95%;\n margin: auto;\n      display: flex;\n    justify-content: space-between;\n    align-items: center;\n        padding: 2px 1px 6px 1px;\n gap: 10px;\n      }\n      .scan-info img {\n        width: 80px;\n        height: 80px;\n      }\n      .doc-text {\n        font-size: 10px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1.3;\n      }\n      .doc-column1 {\n        display: flex;\n        flex-direction: column;\n      }\n      .doc-column2 {\n        display: flex;\n        flex-direction: column;\n      }\n      .doc-column3 {\n        display: flex;\n        flex-direction: column;\n      }\n      .doc-column4 {\n        display: flex;\n        flex-direction: column;\n      }\n      .footerAddressInfo {\n        width: 100%;\n        height: 70px;\n        display: flex;\n        padding: 0 0 6px 15px;\n      }\n      .approvedInfo {\n   padding-left:18px;  \n     display: flex;\n        flex-direction: column;\n      }\n      .approvedRow {\n        width: 100%;\n        display: flex;\n      }\n      .approvedLabel {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 10px;\n        color: #010101;\n        font-weight: 600;\n        line-height: 1.3;\n      }\n      .approvedName {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 10px;\n        color: #000000;\n        font-weight: 600;\n        line-height: 1.3;\n      }\n      .printDateRow {\n        width: 100%;\n        display: flex;\n      }\n      .printDateLabel {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 10px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.1;\n      }\n      .printDateText {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 10px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.1;\n      }\n      .labAddressInfo {\n        width: 100%;\n    justify-content:center;    \n    display: flex;\n        flex-direction: column;\n        align-items: center;\n      }\n      .labAddressText {\n        font-size: 11px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.6;\n        text-align: center;\n      }\n      .labAddressText span {\n        color: #57b0d8;\n      }\n      .pageInfo {\n        width: 25%;\n        display: flex;\n        justify-content: flex-end;\n        position: relative;\n      }\n      .pageLogoInfo {\n        width: 50px;\n        height: 70px;\n        position: absolute;\n        bottom: 5px;\n        left: 0;\n      }\n      .pageText {\n        font-family: "Times New Roman", Times, serif;\n        font-size: 18px;\n        color: #000000;\n        font-weight: 400;\n        line-height: 1.1;\n        padding-right: 55px;\n      }\n      .footerBlueLine {\n        width: 100%;\n        height: 22px;\n        background: #58b0d8;\n        position: relative;\n      }\n      .footerBlueLine::before {\n        content: "";\n        position: absolute;\n        top: -76px;\n        right: 0;\n        border-top: 38px solid transparent;\n        border-left: 38px solid transparent;\n        border-bottom: 38px solid #58b0d8;\n        border-right: 38px solid #58b0d8;\n      }\n @media print {\n .container,.container-sm {\n max-width: 100%;\n} \n}  ',
        }}
      />
      <div
        ref={componentRef}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "1050px",
          padding: "20px",
          boxSizing: "border-box",
          justifyContent: "space-between",
        }}
        className="container"
        id="content-id"
      >
        {data?.data?.data?.length > 0 &&
        data?.data?.data[0]?.header === true ? (
          <>
            {display_header == "true" && (
              <>
                <div className="main-header-container">
                  <header
                    className="headerImage"
                    style={{
                      visibility:
                        display_banner == "true" ? "visible" : "hidden",
                    }}
                  >
                    <div className="coverImage">
                      <img
                        src={`data:image/png;base64,${data?.data?.data[0]?.file_data}`}
                        alt="Custom Image"
                      />
                    </div>
                  </header>
                  <div className="header-container">
                    <section className="barcodeInfo">
                      <div className="barcodeImage">
                        <img
                          src={`data:image/png;base64,${pdfData?.appointment_barcode_img}`}
                          width={200}
                          height={40}
                        />
                      </div>
                    </section>
                    <section className="can-details-section">
                      <div className="can-column-one">
                        <div className="can-detail-row">
                          <div className="can-detail-label">Name:</div>
                          <div className="can-detail-text">
                            {/* <strong>{pdfData?.patient_name_id || ""}</strong> */}
                            <strong>{"Dummy"}</strong>
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Age/Gender:</div>
                          <div className="can-detail-text">
                            {ageDetails?.years} Y / {ageDetails?.months} M /{" "}
                            {ageDetails?.days} D{" "}
                            {pdfData?.patient_obj?.gender || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Patient ID:</div>
                          <div className="can-detail-text">
                            {pdfData?.patient_name_id || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Barcode No:</div>
                          <div className="can-detail-text">
                            {pdfData?.appointment_investigation_obj[0]
                              ?.barcode_no || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Refer By:</div>
                          <div className="can-detail-text">
                            {pdfData?.refer_doctor_name || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Laborator Name:
                          </div>
                          <div className="can-detail-text">
                            {pdfData?.lab_name || ""}
                          </div>
                        </div>
                      </div>
                      <div className="can-column-two">
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Registration No:
                          </div>
                          <div className="can-detail-text">
                            {pdfData?.appointment_investigation_obj[0]
                              ?.appointment_id || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Registration Date:
                          </div>
                          <div className="can-detail-text">
                            <div className="can-detail-text">
                              {pdfData?.appointment_detail_obj?.map(
                                (item, index) => (
                                  <div key={index}>
                                    {convertTimestampToDatetimeLocal(
                                      item?.generated,
                                      item?.show_date
                                    ) || ""}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Receiving Date:
                          </div>
                          {pdfData?.appointment_investigation_obj?.[0]
                            ?.appointment_investigation_status_obj
                            ?.received_date
                            ? convertTimestampToDatetimeLocal(
                                pdfData.appointment_investigation_obj[0]
                                  .appointment_investigation_status_obj
                                  .received_date
                              )
                            : ""}
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Reporting Date:
                          </div>
                          <div className="can-detail-text">
                            {/* {pdfData?.appointment_investigation_obj?.map((investigation) =>
                              investigation?.appointment_investigation_status_obj?.reporting_date
                                ? convertTimestampToDatetimeLocal(investigation.appointment_investigation_status_obj.reporting_date)
                                : ""
                            )} */}
                            {pdfData?.appointment_investigation_obj?.[0]
                              ?.appointment_investigation_status_obj
                              ?.reporting_date
                              ? convertTimestampToDatetimeLocal(
                                  pdfData.appointment_investigation_obj[0]
                                    .appointment_investigation_status_obj
                                    .reporting_date
                                )
                              : ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Panel Code:</div>
                          <div className="can-detail-text">
                            {pdfData?.franchise_name || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Center:</div>
                          <div className="can-detail-text">
                            {pdfData?.zone_name || ""}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {display_header == "true" && (
              <>
                <div className="main-header-container">
                  <header
                    className="headerImage"
                    style={{
                      visibility:
                        display_banner == "true" ? "visible" : "hidden",
                    }}
                  >
                    <div>
                      <img
                        src="../assets/pdfimg/header_image_with_logo.svg"
                        alt="Medicity"
                      />
                    </div>
                  </header>
                  <div className="header-container">
                    <section className="barcodeInfo">
                      {/* <div className="barcodeImage">
                        <img
                          src={`data:image/png;base64,${barcode_detail?.barcode_data[0]?.barcode_image}`}
                          width={200}
                          height={40}
                        />
                      </div> */}
                      <div className="barcodeImages">
                        {/* {barcode_detail?.barcode_data.map((barcode, index) => ( */}
                        <div className="barcodeImage">
                          <img
                            // src={`data:image/png;base64,${barcode.barcode_image}`}
                            src={`data:image/png;base64,${pdfData?.appointment_barcode_img}`}
                            width={200}
                            height={40}
                            // alt={`Barcode ${index + 1}`}
                          />
                        </div>
                        {/* ))} */}
                      </div>
                    </section>
                    <section className="can-details-section">
                      <div className="can-column-one">
                        <div className="can-detail-row">
                          <div className="can-detail-label">Name:</div>
                          <div className="can-detail-text">
                            {/* <strong>{pdfData?.patient_name_id || ""}</strong> */}
                            <strong>{"Dummy"}</strong>
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Age/Gender:</div>
                          <div className="can-detail-text">
                            {ageDetails?.years} Y / {ageDetails?.months} M /{" "}
                            {ageDetails?.days} D{" "}
                            {pdfData?.patient_obj?.gender || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Patient ID:</div>
                          <div className="can-detail-text">
                            {pdfData?.patient_name_id || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Barcode No:</div>
                          <div className="can-detail-text">
                            {pdfData?.appointment_investigation_obj[0]
                              ?.barcode_no || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Refer By:</div>
                          <div className="can-detail-text">
                            {pdfData?.refer_doctor_name || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Laborator Name:
                          </div>
                          <div className="can-detail-text">
                            {pdfData?.lab_name || ""}
                          </div>
                        </div>
                      </div>
                      <div className="can-column-two">
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Registration No:
                          </div>
                          <div className="can-detail-text">
                            {pdfData?.appointment_investigation_obj[0]
                              ?.appointment_id || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Registration Date:
                          </div>
                          {/* <div className="can-detail-text">
                            {convertTimestampToDatetimeLocal(
                              pdfData?.appointment_detail_obj[0]?.generated
                            ) || ""}
                          </div> */}
                          {/* <div className="can-detail-text">
                            {pdfData?.appointment_detail_obj?.map((item, index) => (
                              <div key={index}>
                                {convertTimestampToDatetimeLocal(item.generated) || ""}
                              </div>
                            ))}
                          </div> */}
                          <div className="can-detail-text">
                            {convertTimestampToDatetimeLocal(
                              pdfData?.generated
                            )}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Receiving Date:
                          </div>
                          <div className="can-detail-text">
                            {pdfData?.appointment_investigation_obj?.[0]
                              ?.appointment_investigation_status_obj
                              ?.received_date
                              ? convertTimestampToDatetimeLocal(
                                  pdfData.appointment_investigation_obj[0]
                                    .appointment_investigation_status_obj
                                    .received_date
                                )
                              : ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">
                            Reporting Date:
                          </div>
                          <div className="can-detail-text">
                            {/* {pdfData?.appointment_investigation_obj?.map((investigation) =>
                              investigation?.appointment_investigation_status_obj?.reporting_date
                                ? convertTimestampToDatetimeLocal(investigation.appointment_investigation_status_obj.reporting_date)
                                : ""
                            )} */}
                            {pdfData?.appointment_investigation_obj?.[0]
                              ?.appointment_investigation_status_obj
                              ?.reporting_date
                              ? convertTimestampToDatetimeLocal(
                                  pdfData.appointment_investigation_obj[0]
                                    .appointment_investigation_status_obj
                                    .reporting_date
                                )
                              : ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Panel Code:</div>
                          <div className="can-detail-text">
                            {pdfData?.franchise_name || ""}
                          </div>
                        </div>
                        <div className="can-detail-row">
                          <div className="can-detail-label">Center:</div>
                          <div className="can-detail-text">
                            {pdfData?.zone_name || ""}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {display_address === "true" && (
          <>
            <main className="mainSection">
              <section>
                {groupedData &&
                  Object.entries(groupedData)?.map(
                    ([departmentName, investigations], departmentIndex) => {
                      const departmentPageBreakStyle =
                        departmentIndex > 0
                          ? { pageBreakBefore: "always" }
                          : undefined;

                      return (
                        <div
                          key={departmentIndex}
                          style={departmentPageBreakStyle}
                          className="department-container"
                        >
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                            }}
                          >
                            <thead>
                              <tr>
                                <td colSpan="4">
                                  <div className="dep-title">
                                    DEPARTMENT OF{" "}
                                    {investigations[0]?.lab_department_name}
                                  </div>
                                  <div className="dep-title">
                                    {
                                      investigations[0]?.package_obj
                                        ?.package_name
                                    }
                                  </div>

                                  <div className="rep-head-info">
                                    <div className="rep-column-1">
                                      Test Name
                                    </div>
                                    <div className="rep-column-2">Result</div>
                                    <div className="rep-column-3">Unit</div>
                                    <div className="rep-column-4">
                                      Biological Ref.Interval
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </thead>
                            <tbody>
                              {investigations?.map((item, index) => {
                                const isLastInvestigation =
                                  index === investigations.length - 1;
                                const isLastDepartment =
                                  departmentIndex ===
                                  Object.entries(groupedData).length - 1;
                                const isCompletelyLastItem =
                                  isLastInvestigation && isLastDepartment;
                                const observationsWithValues =
                                  item?.observations_obj?.filter(
                                    (data) =>
                                      data?.header === true ||
                                      (data?.observation_value !== null &&
                                        data?.observation_value !== "")
                                  ) || [];

                                let hasRenderedInvestigationName = false;

                                const pageBreakStyle = item?.print_separate
                                  ? {
                                      pageBreakBefore: "always",
                                      ...(isLastInvestigation
                                        ? {}
                                        : { pageBreakAfter: "always" }),
                                    }
                                  : undefined;

                                return (
                                  <tr key={item?.investigation_id}>
                                    <td colSpan="4">
                                      <div style={pageBreakStyle}>
                                        <style
                                          dangerouslySetInnerHTML={{
                                            __html: `
                                  @media print {
                                    thead { display: table-header-group; }
                                    tfoot { display: table-footer-group; }
                                  }
                                `,
                                          }}
                                        />

                                        {!hasRenderedInvestigationName &&
                                          item?.show_name_in_patient_report && (
                                            <div
                                              className="rep-details-row"
                                              style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginBottom: "8px",
                                              }}
                                            >
                                              <div
                                                className="name-title"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                }}
                                              >
                                                {item?.investigation_name || ""}
                                              </div>
                                            </div>
                                          )}
                                        {(hasRenderedInvestigationName = true)}

                                        {observationsWithValues?.map(
                                          (data, observationIndex, arr) => {
                                            const isLastObservation =
                                              observationIndex ===
                                              arr.length - 1;

                                            return (
                                              <div
                                                key={data?.observation_id}
                                                style={{
                                                  pageBreakInside: "avoid",
                                                  breakInside: "avoid",
                                                }}
                                                className="observation-container"
                                              >
                                                {data?.header ? (
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      fontWeight: "bold",
                                                      textTransform:
                                                        "uppercase",
                                                      textDecoration:
                                                        "underline",
                                                      color: "black",
                                                      marginBottom: ".50rem",
                                                    }}
                                                  >
                                                    {data?.observation_name ||
                                                      ""}
                                                  </div>
                                                ) : data?.template_obj ? (
                                                  <div
                                                    style={{ margin: "15px 0" }}
                                                  >
                                                    {/* <div
                                                      style={{
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        textAlign: "center",
                                                        marginBottom: "10px",
                                                        textTransform:
                                                          "uppercase",
                                                        color: "black",
                                                      }}
                                                    >
                                                      {data?.template_obj
                                                        ?.template || ""}
                                                    </div> */}
                                                    <div
                                                      style={{
                                                        color: "black",
                                                        fontFamily:
                                                          "Arial, sans-serif",
                                                        fontSize: "12px",
                                                        textAlign: "left",
                                                      }}
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          data?.template_obj
                                                            ?.template_description ||
                                                          "",
                                                      }}
                                                    />
                                                  </div>
                                                ) : (
                                                  <>
                                                    <div className="rep-details-row mb-1">
                                                      <div className="rep-column-1">
                                                        <div
                                                          className="test-name"
                                                          style={{
                                                            fontSize: "10px",
                                                          }}
                                                        >
                                                          {data?.observation_name ||
                                                            ""}
                                                        </div>
                                                        <div
                                                          className="test-name-details"
                                                          style={{
                                                            fontSize: "10px",
                                                          }}
                                                        >
                                                          {data?.method_name ||
                                                            ""}
                                                        </div>

                                                        {/* Sample type only on the last observation */}
                                                        {isLastObservation && (
                                                          <>
                                                            {Array.isArray(
                                                              item?.sample_type_obj
                                                            ) &&
                                                              item
                                                                .sample_type_obj
                                                                .length > 0 && (
                                                                <div className="rep-sample-type">
                                                                  <div
                                                                    className="rep-test-type"
                                                                    style={{
                                                                      fontSize:
                                                                        "8px",
                                                                      fontWeight:
                                                                        "200",
                                                                    }}
                                                                  >
                                                                    Sample Type:{" "}
                                                                    <span>
                                                                      {item.sample_type_obj
                                                                        ?.map(
                                                                          (
                                                                            sample
                                                                          ) =>
                                                                            sample.sample_type
                                                                        )
                                                                        .join(
                                                                          ", "
                                                                        )}
                                                                    </span>
                                                                  </div>
                                                                </div>
                                                              )}
                                                          </>
                                                        )}

                                                        {data?.machine_name && (
                                                          <div
                                                            className="rep-test-details"
                                                            style={{
                                                              fontSize: "10px",
                                                            }}
                                                          >
                                                            Test has been
                                                            performed on{" "}
                                                            {data?.machine_name ||
                                                              ""}
                                                          </div>
                                                        )}
                                                      </div>

                                                      <div
                                                        className="rep-column-2"
                                                        style={{
                                                          fontSize: "10px",
                                                          fontWeight:
                                                            !isNaN(
                                                              Number(
                                                                data?.observation_value
                                                              )
                                                            ) &&
                                                            !isNaN(
                                                              Number(
                                                                data?.min_reading
                                                              )
                                                            ) &&
                                                            !isNaN(
                                                              Number(
                                                                data?.max_reading
                                                              )
                                                            ) &&
                                                            (Number(
                                                              data?.observation_value
                                                            ) <
                                                              Number(
                                                                data?.min_reading
                                                              ) ||
                                                              Number(
                                                                data?.observation_value
                                                              ) >
                                                                Number(
                                                                  data?.max_reading
                                                                ))
                                                              ? "bold"
                                                              : "400",
                                                        }}
                                                      >
                                                        {data?.observation_value ||
                                                          ""}
                                                      </div>

                                                      <div className="rep-column-3">
                                                        {data?.unit || ""}
                                                      </div>

                                                      <div className="rep-column-4">
                                                        {data?.display_reading
                                                          ?.split("\n")
                                                          ?.map(
                                                            (
                                                              line,
                                                              lineIndex
                                                            ) => (
                                                              <div
                                                                key={lineIndex}
                                                              >
                                                                {line}
                                                              </div>
                                                            )
                                                          )}
                                                      </div>
                                                    </div>

                                                    {/* Comments and interpretation only on the last observation */}
                                                    {isLastObservation && (
                                                      <>
                                                        {data?.comments && (
                                                          <div
                                                            style={{
                                                              color: "black",
                                                              textAlign:
                                                                "justify",
                                                              fontFamily:
                                                                "Arial, sans-serif",
                                                              fontSize: "12px",
                                                              marginTop: "2px",
                                                            }}
                                                          >
                                                            <strong
                                                              style={{
                                                                color: "black",
                                                                fontFamily:
                                                                  "Arial, sans-serif",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              Comments:
                                                            </strong>
                                                            <br />
                                                            {data?.comments
                                                              ?.split("\n")
                                                              ?.map(
                                                                (
                                                                  line,
                                                                  lineIndex
                                                                ) => (
                                                                  <React.Fragment
                                                                    key={
                                                                      lineIndex
                                                                    }
                                                                  >
                                                                    {line.trim() !==
                                                                    "" ? (
                                                                      line
                                                                    ) : (
                                                                      <>
                                                                        &nbsp;
                                                                      </>
                                                                    )}
                                                                    <br />
                                                                  </React.Fragment>
                                                                )
                                                              )}
                                                          </div>
                                                        )}

                                                        {data?.interpretation && (
                                                          <div
                                                            style={{
                                                              color: "black",
                                                              textAlign:
                                                                "justify",
                                                              fontFamily:
                                                                "Arial, sans-serif",
                                                              fontSize: "12px",
                                                              marginTop: "2px",
                                                            }}
                                                          >
                                                            <strong
                                                              style={{
                                                                color: "black",
                                                                fontFamily:
                                                                  "Arial, sans-serif",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              Interpretation:
                                                            </strong>
                                                            <br />
                                                            {data?.interpretation
                                                              ?.split("\n")
                                                              ?.map(
                                                                (
                                                                  line,
                                                                  lineIndex
                                                                ) => (
                                                                  <React.Fragment
                                                                    key={
                                                                      lineIndex
                                                                    }
                                                                  >
                                                                    {line.trim() !==
                                                                    "" ? (
                                                                      line
                                                                    ) : (
                                                                      <>
                                                                        &nbsp;
                                                                      </>
                                                                    )}
                                                                    <br />
                                                                  </React.Fragment>
                                                                )
                                                              )}
                                                          </div>
                                                        )}
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            );
                                          }
                                        )}

                                        {item?.is_culture &&
                                          item?.observations_organism_obj &&
                                          item.observations_organism_obj
                                            .length > 0 && (
                                            <div
                                              style={{
                                                marginTop: "20px",
                                                marginBottom: "20px",
                                              }}
                                            >
                                              <table
                                                style={{
                                                  width: "80%",
                                                  borderCollapse: "collapse",
                                                  border: "1px solid #333",
                                                  color: "#000",
                                                }}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid #333",
                                                        padding: "8px",
                                                        fontSize: "10px",
                                                      }}
                                                    >
                                                      Sr. No.
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid #333",
                                                        padding: "8px",
                                                        fontSize: "10px",
                                                      }}
                                                    >
                                                      Observation
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid #333",
                                                        padding: "8px",
                                                        fontSize: "10px",
                                                      }}
                                                    >
                                                      Zone of Inhibition
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {item.observations_organism_obj?.map(
                                                    (organism, orgIndex) => (
                                                      <tr key={orgIndex}>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid #333",
                                                            padding: "8px",
                                                            fontSize: "10px",
                                                            width: "10%",
                                                          }}
                                                        >
                                                          {orgIndex + 1}
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid #333",
                                                            padding: "8px",
                                                            fontSize: "10px",
                                                            width: "45%",
                                                          }}
                                                        >
                                                          {organism
                                                            ?.observation_obj
                                                            ?.observation || ""}
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid #333",
                                                            padding: "8px",
                                                            fontSize: "10px",
                                                            width: "30%",
                                                            fontWeight:
                                                              !isNaN(
                                                                Number(
                                                                  organism?.observation_value
                                                                )
                                                              ) &&
                                                              !isNaN(
                                                                Number(
                                                                  organism?.min_reading
                                                                )
                                                              ) &&
                                                              !isNaN(
                                                                Number(
                                                                  organism?.max_reading
                                                                )
                                                              ) &&
                                                              (Number(
                                                                organism?.observation_value
                                                              ) <
                                                                Number(
                                                                  organism?.min_reading
                                                                ) ||
                                                                Number(
                                                                  organism?.observation_value
                                                                ) >
                                                                  Number(
                                                                    organism?.max_reading
                                                                  ))
                                                                ? "bold"
                                                                : "400",
                                                          }}
                                                        >
                                                          {organism?.observation_value ||
                                                            ""}
                                                          ,
                                                          {
                                                            organism?.observation_value_mac_reading
                                                          }
                                                        </td>
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}

                                        {isCompletelyLastItem && (
                                          <div className="rep-end-report">
                                            <div className="rep-end-text">
                                              *** End Of Report ***
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                  )}

                {/* <div className="rep-end-report">
                  <div className="rep-end-text">*** End Of Report ***</div>
                </div> */}

                {pdfData?.appointment_investigation_obj?.map(
                  (app_inv, appIndex) =>
                    app_inv.appointment_investigation_file_data?.map(
                      (file, index) => (
                        <div
                          key={`${appIndex}-${index}`}
                          style={{
                            marginTop: "20px",
                            pageBreakAfter: "always",
                          }}
                        >
                          <div
                            style={{ fontWeight: "bold", marginBottom: "10px" }}
                          >
                            {file.file_name}
                          </div>
                          <iframe
                            src={`data:application/pdf;base64,${file.file_data}`}
                            style={{
                              width: "100%",
                              height: "500px",
                              border: "1px solid #ccc",
                            }}
                            title={file.file_name}
                          />
                        </div>
                      )
                    )
                )}
              </section>
            </main>
          </>
        )}
        {data?.data?.data?.length > 0 &&
        data?.data?.data[0]?.footer === true ? (
          <>
            <div className="footer-container">
              <div className="sample-report-indicator">
                <div className="sample-badge">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"
                      fill="#FF6B6B"
                    />
                    <path
                      d="M12 7L12.5 10.5L16 11L12.5 11.5L12 15L11.5 11.5L8 11L11.5 10.5L12 7Z"
                      fill="#FFF"
                    />
                  </svg>
                  <span>SAMPLE REPORT</span>
                </div>
              </div>
              {display_footer == "true" && (
                <>
                  {pdfData?.franchise_obj?.payment_mode === "CREDIT" && (
                    <div className="footerTopRow">Sample Source:OSS.</div>
                  )}
                  <div className="footer-doc-details">
                    <div className="scan-info">
                      <img src="../assets/pdfimg/medicity-scan.png" />
                    </div>
                    {/* <div className="doctor-info">
                      <img src="../assets/pdfimg/doctors_image.jpg" />
                    </div> */}
                    <div className="doctor-info" style={{ width: "100%" }}>
                      {doctorSigData?.data && (
                        <img
                          src={`data:image/jpeg;base64,${doctorSigData.data}`}
                          alt="Doctor Signature"
                        />
                      )}
                    </div>
                    {/* <div className="doc-column1 doc-text">
                      <span>Dr. Debashri Mondal</span>
                      <span>MD (Pathology)</span>
                      <span>Consultant Pathologist</span>
                    </div>
                    <div className="doc-column2 doc-text">
                      <span>Dr. S. Sen</span>
                      <span>MD (Biochemistry)</span>
                      <span>Consultant Biochemist</span>
                    </div>
                    <div className="doc-column3 doc-text">
                      <span>Dr. S. SAHA</span>
                      <span>MD (Pathology) </span>
                      <span>Consultant Pathologist</span>
                    </div>
                    <div className="doc-column4 doc-text">
                      <span>{pdfData?.refer_doctor_name || ""}</span>
                      <span>Dr. A. Saha</span>
                      <span>MD (Biochemistry)</span>
                      <span>Consultant Biochemist</span>
                      <span>Registration No. -56425</span>
                    </div> */}
                  </div>
                  <div className="approvedInfo">
                    <div className="approvedRow">
                      <div className="approvedLabel">Approved Name:</div>
                      <div className="approvedName">
                        {pdfData?.appointment_investigation_obj?.[0]
                          ?.appointment_investigation_status_obj?.approved_by
                          ?.employee_master?.[0]?.employee_name || ""}
                      </div>
                    </div>
                    <div className="printDateRow">
                      <div className="printDateLabel">Print Date:</div>
                      <div className="printDateText">
                        {(() => {
                          const today = new Date();
                          const dd = String(today.getDate()).padStart(2, "0");
                          const mm = String(today.getMonth() + 1).padStart(
                            2,
                            "0"
                          );
                          const yyyy = today.getFullYear();
                          return `${dd}-${mm}-${yyyy}`;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      visibility:
                        display_footer_banner == "true" ? "visible" : "hidden",
                    }}
                  >
                    <div className="footerAddressInfo">
                      {/* <div className="approvedInfo">
                        <div className="approvedRow">
                          <div className="approvedLabel">Approved Name:</div>
                          <div className="approvedName">
                            {pdfData?.appointment_investigation_obj?.[0]
                              ?.appointment_investigation_status_obj
                              ?.approved_by?.employee_master?.[0]
                              ?.employee_name || ""}
                          </div>
                        </div>
                        <div className="printDateRow">
                          <div className="printDateLabel">Print Date:</div>
                          <div className="printDateText">10-07-2024</div>
                        </div>
                      </div> */}
                      <div className="labAddressInfo">
                        <div className="labAddressText">
                          {pdfData?.zone_obj?.address}
                        </div>
                        <div className="labAddressText">
                          <span>Type Room:</span> 900 799 0168
                          <span style={{ marginLeft: "5px" }}>
                            Mobile:
                          </span>{" "}
                          {pdfData?.zone_obj?.mobile_number}
                        </div>
                        <div className="labAddressText">
                          <span>Email:</span> {pdfData?.zone_obj?.email}
                          <span style={{ marginLeft: "5px" }}>
                            Website:
                          </span>{" "}
                          {pdfData?.zone_obj?.website}
                        </div>
                      </div>
                      <div className="pageInfo">
                        {pdfData?.appointment_investigation_obj?.map(
                          (item, index) =>
                            item?.is_nabl && (
                              <div className="pageLogoInfo" key={index}>
                                <img
                                  src="../assets/pdfimg/footer-mc-logo.png"
                                  alt="Footer Logo"
                                />
                              </div>
                            )
                        )}
                      </div>
                    </div>
                    <div className="footerImageContainer">
                      <img
                        src={`data:image/png;base64,${data?.data?.data[0]?.file_data}`}
                        alt="Dynamic Footer"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="footer-container">
              <div className="sample-report-indicator">
                <div className="sample-badge">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"
                      fill="#FF6B6B"
                    />
                    <path
                      d="M12 7L12.5 10.5L16 11L12.5 11.5L12 15L11.5 11.5L8 11L11.5 10.5L12 7Z"
                      fill="#FFF"
                    />
                  </svg>
                  <span>SAMPLE REPORT</span>
                </div>
              </div>
              {display_footer == "true" && (
                <>
                  {pdfData?.franchise_obj?.payment_mode === "CREDIT" && (
                    <div className="footerTopRow">Sample Source:OSS.</div>
                  )}
                  <div className="footer-doc-details">
                    <div className="scan-info">
                      <img src="../assets/pdfimg/medicity-scan.png" />
                    </div>
                    {/* <div className="doctor-info" style={{ width: "100%" }}>
                      <img src="../assets/pdfimg/doctors_image.jpg" />
                    </div> */}
                    <div className="doctor-info" style={{ width: "100%" }}>
                      {doctorSigData?.data && (
                        <img
                          src={`data:image/jpeg;base64,${doctorSigData.data}`}
                          alt="Doctor Signature"
                        />
                      )}
                    </div>
                    {/* <div className="doc-column1 doc-text">
                      <span>Dr. Debashri Mondal</span>
                      <span>MD (Pathology)</span>
                      <span>Consultant Pathologist</span>
                    </div>
                    <div className="doc-column2 doc-text">
                      <span>Dr. S. Sen</span>
                      <span>MD (Biochemistry)</span>
                      <span>Consultant Biochemist</span>
                    </div>
                    <div className="doc-column3 doc-text">
                      <span>Dr. S. SAHA</span>
                      <span>MD (Pathology) </span>
                      <span>Consultant Pathologist</span>
                    </div>
                    <div className="doc-column4 doc-text">
                      <span>{pdfData?.refer_doctor_name || ""}</span>
                      <span>Dr. A. Saha</span>
                      <span>MD (Biochemistry)</span>
                      <span>Consultant Biochemist</span>
                      <span>Registration No. -56425</span>
                    </div> */}
                  </div>
                  <div className="approvedInfo">
                    <div className="approvedRow">
                      <div className="approvedLabel">Approved Name:</div>
                      <div className="approvedName">
                        {pdfData?.appointment_investigation_obj?.[0]
                          ?.appointment_investigation_status_obj?.approved_by
                          ?.employee_master?.[0]?.employee_name || ""}
                      </div>
                    </div>
                    <div className="printDateRow">
                      <div className="printDateLabel">Print Date:</div>
                      <div className="printDateText">
                        {(() => {
                          const today = new Date();
                          const dd = String(today.getDate()).padStart(2, "0");
                          const mm = String(today.getMonth() + 1).padStart(
                            2,
                            "0"
                          );
                          const yyyy = today.getFullYear();
                          return `${dd}-${mm}-${yyyy}`;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      visibility:
                        display_footer_banner == "true" ? "visible" : "hidden",
                    }}
                  >
                    <div className="footerAddressInfo">
                      {/* <div className="approvedInfo">
                        <div className="approvedRow">
                          <div className="approvedLabel">Approved Name:</div>
                          <div className="approvedName">
                            {pdfData?.appointment_investigation_obj?.[0]
                              ?.appointment_investigation_status_obj
                              ?.approved_by?.employee_master?.[0]
                              ?.employee_name || ""}
                          </div>
                        </div>
                        <div className="printDateRow">
                          <div className="printDateLabel">Print Date:</div>
                          <div className="printDateText">10-07-2024</div>
                        </div>
                      </div> */}
                      <div className="labAddressInfo">
                        <div className="labAddressText">
                          {pdfData?.zone_obj?.address}
                        </div>
                        <div className="labAddressText">
                          <span>Type Room:</span> 900 799 0168
                          <span style={{ marginLeft: "5px" }}>
                            Mobile:
                          </span>{" "}
                          {pdfData?.zone_obj?.mobile_number}
                        </div>
                        <div className="labAddressText">
                          <span>Email:</span> {pdfData?.zone_obj?.email}
                          <span style={{ marginLeft: "5px" }}>
                            Website:
                          </span>{" "}
                          {pdfData?.zone_obj?.website}
                        </div>
                      </div>
                      <div className="pageInfo">
                        {pdfData?.appointment_investigation_obj?.map(
                          (item, index) =>
                            item?.is_nabl && (
                              <div className="pageLogoInfo" key={index}>
                                <img
                                  src="../assets/pdfimg/footer-mc-logo.png"
                                  alt="Footer Logo"
                                />
                              </div>
                            )
                        )}
                      </div>
                    </div>
                    <div className="footerBlueLine"></div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicePage;
